import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useConsumeInvitation } from '../api/queries/invitations';
import { ApiError } from '../api/client';

export function AcceptInvitationPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const consume = useConsumeInvitation();

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('Invitation token missing.');
      return;
    }
    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    try {
      await consume.mutateAsync({ token, password, display_name: displayName.trim() });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('Invitation not found.');
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        setError('Invitation has already been used.');
        return;
      }
      if (err instanceof ApiError && err.status === 410) {
        setError('Invitation has expired. Ask your administrator to send a new one.');
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { details?: { password?: string } } | null;
        setError(body?.details?.password ?? 'Password does not meet policy.');
        return;
      }
      setError('Could not complete signup. Please try again.');
    }
  };

  if (done) {
    return (
      <section className="auth-section" style={{ maxWidth: 520, margin: '4rem auto', padding: 24 }}>
        <h2>Welcome Aboard</h2>
        <p>Your account has been created. You can now sign in.</p>
        <button type="button" className="btn-cyan-primary" onClick={() => navigate('/login', { replace: true })}>
          Go to sign in
        </button>
      </section>
    );
  }

  return (
    <section className="auth-section" style={{ maxWidth: 520, margin: '4rem auto', padding: 24 }}>
      <h2>Accept Invitation</h2>
      <p style={{ color: 'var(--color-silver-metallic)' }}>
        Set your display name and password to finish creating your account.
      </p>
      <form onSubmit={onSubmit}>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span>Display name</span>
          <input
            type="text"
            className="form-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            minLength={1}
            maxLength={200}
          />
        </label>
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span>Password (≥ 12 chars)</span>
          <input
            type="password"
            className="form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={12}
            required
            autoComplete="new-password"
          />
        </label>
        {error && <p className="set-error" role="alert">{error}</p>}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 16 }}>
          <Link to="/login" className="btn-text">Already have an account?</Link>
          <button
            type="submit"
            className="btn-cyan-primary"
            disabled={consume.isPending || password.length < 12 || displayName.trim().length === 0}
          >
            {consume.isPending ? 'Creating…' : 'Create account'}
          </button>
        </div>
      </form>
    </section>
  );
}
